import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Audit-trail viewer surface (T-0023 / E01-F05).
 *
 * The engagement overview hero (T-0097) needs the recent audit feed for
 * the "Critical signals" subsection. The ticket §6 ("Risks and unknowns")
 * acknowledges that the backend doesn't yet expose a unified "recent
 * signals" endpoint that fuses finding-severity-crossed +
 * deviation-logged + score-threshold-crossed; until that lands, we pull
 * the most-recent N audit entries and surface them as a generic recent-
 * events list. The backend's audit endpoint accepts a constrained
 * `event_type` filter (`create | update | delete | access_denied |
 * override | proposal`) — those don't map directly to the "critical
 * signal" operation strings like `finding_severity_change`, so we omit
 * the filter on the client and let the user / future ticket refine.
 *
 * Limitations carried forward as backend follow-ups (flag in §Final
 * report):
 *   - No GET /engagements/:id/recent-signals composite endpoint.
 *   - No filter for finding/deviation operations on the audit view.
 *   - No "severity" or "is_critical" surface on audit rows.
 */

export const AuditEventSchema = z.object({
  id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  resource_type: z.string(),
  resource_id: z.string(),
  event_type: z.string(),
  actor_user_id: z.string().nullable(),
  correlation_id: z.string(),
  before: z.unknown(),
  after: z.unknown(),
  override_tag: z.string().nullable(),
  override_rationale: z.string().nullable(),
  source_event_topic: z.string().nullable(),
  created_at: z.string(),
  // The record's name and kind (C2-30), so the trail reads "Approved
  // contract fact: Vendor". Optional: an API build without them still parses.
  resource_title: z.string().nullable().optional(),
  resource_subtype: z.string().nullable().optional(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const AuditListSchema = z.object({
  events: z.array(AuditEventSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
});
export type AuditList = z.infer<typeof AuditListSchema>;

export interface ListAuditOpts {
  event_type?: 'create' | 'update' | 'delete' | 'access_denied' | 'override' | 'proposal';
  actor_user_id?: string;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
}

export function listEngagementAudit(
  engagementId: string,
  opts: ListAuditOpts = {},
): Promise<AuditList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/audit`,
    AuditListSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export function useEngagementAuditQuery(
  engagementId: string | undefined,
  opts: ListAuditOpts = {},
) {
  return useQuery({
    queryKey: ['audit', engagementId, opts] as const,
    queryFn: () => listEngagementAudit(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}
